import {
  EnvironmentProviders,
  inject,
  provideAppInitializer
} from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { catchError, first, map, switchMap, timeout } from 'rxjs/operators';
import { LaunchDarklyService } from 'sustainment-component';
import { AuthService } from '../services/auth.service';
import { UserAccountQuery } from '../store/userAccount/user-account.query';

const AUTH_TIMEOUT_MS = 10000;
const LD_FLAGS_TIMEOUT_MS = 5000;

// Routes that run their own Okta login/signup flow. Don't front-run auth there.
const AUTH_FLOW_PATHS = ['/login', '/signup', '/signin'];

export function initializeLdFlags(
  auth: AuthService,
  ld: LaunchDarklyService,
  userAccountQuery: UserAccountQuery
): Observable<void> {
  const path = window.location.pathname;
  if (AUTH_FLOW_PATHS.some((p) => path.startsWith(p))) {
    return of(undefined);
  }

  return auth.isAuthenticated$.pipe(
    first(),
    timeout({ first: AUTH_TIMEOUT_MS, with: () => of(false) }),
    catchError(() => of(false)), // never block bootstrap on an auth error
    switchMap((authenticated) => {
      if (!authenticated) return of(undefined);

      const userAccount = userAccountQuery.getValue()?.userAccount;
      if (!userAccount) return of(undefined);

      ld.initialize(userAccount);

      return timer(0, 50).pipe(
        map(() => ld.flags()),
        first((flags) => flags !== undefined),
        timeout({ first: LD_FLAGS_TIMEOUT_MS, with: () => of(undefined) }),
        map(() => undefined)
      );
    })
  );
}

/** App initializer that runs {@link initializeLdFlags} before initial navigation. */
export const ldFlagsInitializerProvider: EnvironmentProviders =
  provideAppInitializer(() =>
    initializeLdFlags(
      inject(AuthService),
      inject(LaunchDarklyService),
      inject(UserAccountQuery)
    )
  );
